import * as L from "leaflet";
import "leaflet-kml";
import { kml as toGeoJsonKml } from "@mapbox/togeojson";
import type {
  Feature,
  FeatureCollection,
  Geometry,
  GeoJsonObject,
} from "geojson";
import clsx, { ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import JSZip from "jszip";
import { Drawing } from "../context/RulerContext";

export const PE_LAT = { min: -12.0, max: -7.0 };
export const PE_LNG = { min: -44.0, max: -32.0 };

export function isInsidePernambuco(lat: number, lng: number): boolean {
  return (
    lat >= PE_LAT.min &&
    lat <= PE_LAT.max &&
    lng >= PE_LNG.min &&
    lng <= PE_LNG.max
  );
}

export function computeBbox(
  geojson: any
): [number, number, number, number] | null {
  try {
    const bounds = L.geoJSON(geojson).getBounds();
    if (bounds.isValid()) {
      return [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];
    }
  } catch {}
  return null;
}

export type KmlParseResult =
  | { ok: true; doc: Document; geojson: any; safeForPlugin: boolean }
  | { ok: false; error: string };

const UNSUPPORTED_KML_PLUGIN_TAGS = [
  "GroundOverlay",
  "ScreenOverlay",
  "PhotoOverlay",
  "NetworkLink",
];

function getElementsByLocalName(
  node: Document | Element,
  tagName: string
): Element[] {
  const result: Element[] = [];
  if (typeof node.getElementsByTagName === "function") {
    result.push(...Array.from(node.getElementsByTagName(tagName)));
  }
  if (typeof node.getElementsByTagNameNS === "function") {
    result.push(...Array.from(node.getElementsByTagNameNS("*", tagName)));
  }
  return result;
}

function isGroundOverlaySupported(groundOverlay: Element): boolean {
  const latLonBox = getElementsByLocalName(groundOverlay, "LatLonBox")[0];
  if (!latLonBox) {
    return false;
  }

  const requiredFields = ["south", "west", "north", "east"];
  const hasRequiredCoords = requiredFields.every((field) => {
    const fieldElement = getElementsByLocalName(latLonBox, field)[0];
    return !!fieldElement?.textContent?.trim();
  });
  if (!hasRequiredCoords) {
    return false;
  }

  if (getElementsByLocalName(groundOverlay, "LatLonQuad").length > 0) {
    return false;
  }

  const hrefElement = getElementsByLocalName(groundOverlay, "href")[0];
  if (!hrefElement?.textContent?.trim()) {
    return false;
  }

  return true;
}

export const isKmlSafeForPlugin = (doc: Document): boolean => {
  const hasUnsupportedTag = UNSUPPORTED_KML_PLUGIN_TAGS.some((tag) => {
    return getElementsByLocalName(doc, tag).length > 0;
  });

  if (hasUnsupportedTag) {
    return false;
  }

  const groundOverlays = getElementsByLocalName(doc, "GroundOverlay");
  return groundOverlays.every(isGroundOverlaySupported);
};

export const getMimeType = (fileName: string): string => {
  const extension = fileName.split(".").pop()?.toLowerCase() ?? "";
  switch (extension) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "svg":
      return "image/svg+xml";
    case "bmp":
      return "image/bmp";
    case "webp":
      return "image/webp";
    default:
      return "application/octet-stream";
  }
};

export const normalizeKmlResourcePath = (value: string): string =>
  value
    .replace(/^\.\/+/g, "")
    .replace(/\\/g, "/")
    .replace(/[?#].*$/, "")
    .trim()
    .toLowerCase();

function buildKmlResourceCandidates(href: string, basePath?: string): string[] {
  const normalizedHref = normalizeKmlResourcePath(href);
  const candidates = [normalizedHref];

  if (basePath) {
    const normalizedBasePath = normalizeKmlResourcePath(basePath);
    const slashIndex = normalizedBasePath.lastIndexOf("/");
    if (slashIndex !== -1) {
      const baseDir = normalizedBasePath.slice(0, slashIndex + 1);
      const resolved = normalizeKmlResourcePath(baseDir + normalizedHref);
      if (resolved !== normalizedHref) {
        candidates.unshift(resolved);
      }
    }
  }

  return candidates;
}

function embedKmlResources(
  doc: Document,
  resources: Record<string, string>,
  basePath?: string
): void {
  const hrefElements = Array.from(doc.getElementsByTagName("href"));

  hrefElements.forEach((hrefElement) => {
    const originalHref = hrefElement.textContent?.trim() ?? "";
    if (!originalHref) return;
    if (/^data:/i.test(originalHref)) return;
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(originalHref)) return;

    const candidates = buildKmlResourceCandidates(originalHref, basePath);
    for (const candidate of candidates) {
      if (resources[candidate]) {
        hrefElement.textContent = resources[candidate];
        break;
      }
    }
  });
}

/**
 * Parses a raw KML string. Returns the DOM document and its GeoJSON equivalent
 * on success, or a user-friendly error message on failure.
 */
export function parseKmlContent(
  content: string,
  resourceMap?: Record<string, string>,
  basePath?: string
): KmlParseResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/xml");

  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    const msg = parseError.textContent?.trim().slice(0, 120) ?? "erro de XML";
    return { ok: false, error: `KML inválido: ${msg}` };
  }

  if (resourceMap && Object.keys(resourceMap).length > 0) {
    embedKmlResources(doc, resourceMap, basePath);
  }

  const geojson = toGeoJsonKml(doc) as any;
  if (!geojson.features || geojson.features.length === 0) {
    return { ok: false, error: "O arquivo KML não contém feições visíveis." };
  }

  return {
    ok: true,
    doc,
    geojson,
    safeForPlugin: isKmlSafeForPlugin(doc),
  };
}

export type GeoJsonParseResult =
  | { ok: true; geojson: any }
  | { ok: false; error: string };

export function parseGeoJsonContent(content: string): GeoJsonParseResult {
  try {
    const geojson = JSON.parse(content);

    if (!geojson.features || !Array.isArray(geojson.features)) {
      return {
        ok: false,
        error: "GeoJSON inválido: o arquivo não contém o campo 'features'.",
      };
    }

    if (geojson.features.length === 0) {
      return {
        ok: false,
        error: "GeoJSON inválido: o arquivo não contém geometrias válidas.",
      };
    }

    const hasValidGeometry = geojson.features.some(
      (feature: any) => feature.geometry && feature.geometry !== null
    );

    if (!hasValidGeometry) {
      return {
        ok: false,
        error: "GeoJSON inválido: nenhuma feature contém geometria válida.",
      };
    }

    return { ok: true, geojson };
  } catch {
    return {
      ok: false,
      error: "GeoJSON inválido: o arquivo não é um JSON válido.",
    };
  }
}

/**
 * Extracts a single geometry object from a GeoJSON value for use with APIs
 * that expect a plain geometry rather than a FeatureCollection.
 */
export function extractPrimaryGeometry(
  geojson: GeoJsonObject | null | undefined
): Geometry | null {
  if (!geojson) return null;

  if (geojson.type === "FeatureCollection") {
    const collection = geojson as FeatureCollection<Geometry>;
    const polygon = collection.features.find(
      (feature): feature is Feature<Geometry> =>
        !!feature?.geometry &&
        (feature.geometry.type === "Polygon" ||
          feature.geometry.type === "MultiPolygon")
    );
    if (polygon) return polygon.geometry;
    const first = collection.features[0];
    return first?.geometry ?? null;
  }

  if (geojson.type === "Feature") {
    return (geojson as Feature<Geometry>).geometry;
  }

  return geojson as Geometry;
}

const convertUint8ArrayToBase64 = (buffer: Uint8Array): string => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < buffer.length; i += chunkSize) {
    const chunk = buffer.subarray(i, i + chunkSize);
    for (let j = 0; j < chunk.length; j += 1) {
      binary += String.fromCharCode(chunk[j]);
    }
  }
  return btoa(binary);
};

export const buildKmzResourceMap = async (
  zip: JSZip
): Promise<Record<string, string>> => {
  const resourceMap: Record<string, string> = {};
  const files = Object.values(zip.files) as JSZip.JSZipObject[];

  await Promise.all(
    files
      .filter(
        (file) =>
          !file.dir && /\.(png|jpe?g|gif|svg|bmp|webp)$/i.test(file.name)
      )
      .map(async (file) => {
        const data = await file.async("uint8array");
        const key = normalizeKmlResourcePath(file.name);
        resourceMap[key] = `data:${getMimeType(
          file.name
        )};base64,${convertUint8ArrayToBase64(data)}`;
      })
  );

  return resourceMap;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getFirstVertexLatLng(
  geometry: Geometry
): { lat: number; lng: number } | null {
  let ring: number[][] | undefined;

  if (geometry.type === "Polygon") {
    ring = geometry.coordinates[0];
  }
  if (geometry.type === "MultiPolygon") {
    ring = geometry.coordinates[0]?.[0];
  }

  const firstVertex = ring?.[0];
  if (!firstVertex) return null;

  const [lng, lat] = firstVertex;
  return { lat, lng };
}

export const drawingToGeometry = (drawing?: Drawing | null) => {
  if (!drawing) return null;
  if (drawing.layer && typeof (drawing.layer as any).toGeoJSON === "function") {
    const geojson = (drawing.layer as any).toGeoJSON();
    return geojson.geometry;
  }
  return null;
};

export function normalizeLatLng(
  a: number,
  b: number
): { lat: number; lng: number } {
  if (Math.abs(a) > 90) {
    return { lat: b, lng: a };
  }

  const aFitsLat = a >= PE_LAT.min && a <= PE_LAT.max;
  const bFitsLat = b >= PE_LAT.min && b <= PE_LAT.max;
  const aFitsLng = a >= PE_LNG.min && a <= PE_LNG.max;

  if (aFitsLng && bFitsLat && !aFitsLat) {
    return { lat: b, lng: a };
  }

  return { lat: a, lng: b };
}
